import React, { useState } from 'react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';
import { useDropzone } from 'react-dropzone';
import '../styles/FileUpload.css';
import { Check, Trash } from 'lucide-react';
import { uploadToGoogleDrive } from './GoogleDriveUpload';

// Virus scanning function
const scanFileForViruses = async (file) => {
  try {
    // Convert file to base64
    const reader = new FileReader();
    const base64Promise = new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
    reader.readAsDataURL(file);
    const base64Data = await base64Promise;

    const response = await fetch(process.env.REACT_APP_VIRUSTOTAL_SCAN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fileData: base64Data,
        fileName: file.name
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to scan file');
    }

    const data = await response.json();
    if (data.isInfected) {
      throw new Error(data.details || 'File contains viruses');
    }

    console.log(`✅ File "${file.name}" successfully scanned and cleared.`);
    return true;
  } catch (error) {
    console.error('Virus scan error:', error);
    throw error;
  }
};

function FileUpload({ task_id = null }) {
  const { authUserId, selectedWorkspace, selectedWorkspaceId } = useWorkspace();
  const { language } = useLanguage();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleCheckboxChange = (index) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.map((fileObj, i) =>
        i === index ? { ...fileObj, isSensitive: !fileObj.isSensitive } : fileObj
      )
    );
  };

  const handleNoteChange = (index, newNote) => {
    setSelectedFiles((prevFiles) =>
      prevFiles.map((fileObj, i) =>
        i === index ? { ...fileObj, note: newNote } : fileObj
      )
    );
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    if (!selectedFiles || selectedFiles.length === 0) {
      setError(language === 'en' ? "No files selected!" : "Keine Dateien ausgewählt!");
      return;
    }

    if (!authUserId || !selectedWorkspaceId) {
      setError(language === 'en' ? "Invalid session: missing user or workspace." : "Ungültige Sitzung: Benutzer oder Arbeitsbereich fehlt.");
      return;
    }

    setError('');
    setSuccessMessage('');

    const timestamp = new Date().toISOString();
    const orgId = "7e8835a5-5c90-487a-82b0-62758d80435c";
    const bucketName = "7e8835a5_5c90_487a_82b0_62758d80435c";
    const generatedDocIds = [];

    try {
      for (let { file, isSensitive, note } of selectedFiles) {
        const documentId = uuidv4();
        generatedDocIds.push(documentId);
        const filePath = `${selectedWorkspaceId}/${file.name}`;

        const { error: uploadError } = await supabase.storage
          .from(bucketName)
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        const { data: publicUrlData } = supabase.storage.from(bucketName).getPublicUrl(filePath);
        const fileUrl = publicUrlData.publicUrl;

        const documentData = {
          id: documentId,
          name: file.name,
          created_at: timestamp,
          updated_at: timestamp,
          updated_by: authUserId,
          workspace_id: selectedWorkspaceId,
          org_id: "7e8835a5-5c90-487a-82b0-62758d80435c",
          is_sensitive: isSensitive,
          is_deleted: false,
          s3_bucketname: bucketName,
          folder_path: "input/",
          s3_key: selectedWorkspaceId,
          type: file.type,
          note: note
        };

        const { error: insertError } = await supabase.from('documents').insert([documentData]);

        if (insertError) {
          throw insertError;
        }
      }

      if (task_id !== null) {
        const { error: updateError } = await supabase
          .from('tasks')
          .update({ assigned_documents: generatedDocIds })
          .eq('id', task_id);
        if (updateError) {
          throw updateError;
        }
      }

      // Then, upload to Google Drive
      try {
        await uploadToGoogleDrive(selectedFiles, selectedWorkspaceId);
        setSuccessMessage(language === 'en' ? "Documents uploaded successfully!" : "Dokumente erfolgreich hochgeladen!");
      } catch (driveError) {
        console.error('Google Drive upload failed:', driveError);
        setError(language === 'en' ? `Google Drive upload failed: ${driveError.message}` : `Google Drive-Upload fehlgeschlagen: ${driveError.message}`);
      }

      setSelectedFiles([]);
    } catch (err) {
      setError(language === 'en' ? `Upload failed: ${err.message}` : `Upload fehlgeschlagen: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles) => {
      const validFiles = acceptedFiles
        .filter((file) => file.size <= 20 * 1024 * 1024)
        .map((file) => ({ file, isSensitive: false, note: '' }));

      if (validFiles.length > 0) {
        setSelectedFiles(validFiles);
        setError('');
      } else {
        setError(language === 'en' ? 'Maximum supported size is 20MB.' : 'Die maximale unterstützte Größe beträgt 20MB.');
      }
    },
    accept: '.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg',
  });

  return (
    <div className="file-upload-container">
      <div className="upload-box" {...getRootProps()}>
        <input 
          type="file" 
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg" 
          multiple
          hidden 
          {...getInputProps()} 
        />
        <p>{language === 'en' ? 'Drag & drop files here, or click to upload (max: 20MB per file)' : 'Dateien hierher ziehen oder klicken zum Hochladen (Max: 20MB pro Datei)'}</p>
      </div>

      {selectedFiles.length > 0 && (
        <div className="file-list">
          {selectedFiles.map(({ file, isSensitive, note }, index) => (
            <div key={index} className="file-item">
              <div className="file-header">
                <p className="uploaded-file-name">📄 {file.name}</p>
                <button className="remove-file-button" onClick={() => handleRemoveFile(index)}>
                  <Trash size={16} />
                </button>
              </div>
              <div className="upload-note">
                <input 
                  type="text" 
                  placeholder={language === 'en' ? "You can add notes here." : "Sie können hier Notizen hinzufügen."} 
                  value={note}
                  onChange={(e) => handleNoteChange(index, e.target.value)}
                  className="note-input"
                />
              </div>
              <div className="upload-sensitive-checkbox">
                <input 
                  type="checkbox" 
                  id={`sensitiveCheck-${index}`} 
                  checked={isSensitive} 
                  onChange={() => handleCheckboxChange(index)} 
                />
                <label htmlFor={`sensitiveCheck-${index}`}>
                  {language === 'en' ? 'Mark as Sensitive' : 'Als sensibel markieren'}
                </label>
              </div>
            </div>
          ))}
        </div>
      )}
      {error && <p className="upload-error-text">{error}</p>}
      {successMessage && <p className="upload-success-text">{successMessage}</p>}

      {selectedFiles.length > 0 && (
        <>
          <div style={{ clear: 'both', marginTop: '30px' }}></div>
          <button 
            className="upload-submit-button" 
            onClick={handleSubmit}
            disabled={uploading}
          >
            <Check size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
            {uploading 
              ? (language === 'en' ? 'Uploading...' : 'Wird hochgeladen...')
              : (language === 'en' ? 'Submit' : 'Einreichen')}
          </button>
        </>
      )}
    </div>
  );
}

export default FileUpload;
